import { AuthConfig, OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import { filter, firstValueFrom, Observable, ReplaySubject } from 'rxjs';
import { DestroyRef, inject, Injectable, isDevMode } from '@angular/core';
import { CONFIGURATION_SERVICE, IConfigurationService } from '@hubbix/common-components/configuration';
import { IAuthenticationConfiguration } from '@hubbix/common-components/configuration/lib/types/authentication-configuration';
import { IAuthenticationService } from '../interfaces/iauthentication-service.interface';
import { AnyValue } from '@shared/types/any-value.type';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable()
export class AuthenticationService implements IAuthenticationService {
  public readonly isAuthenticated$!: Observable<boolean>;

  public get identityClaims(): Record<string, AnyValue> {
    return this.oauthService.getIdentityClaims();
  }

  private readonly oauthService: OAuthService = inject(OAuthService);

  private readonly configurationService: IConfigurationService = inject(CONFIGURATION_SERVICE);

  private readonly isAuthenticatedSubject$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  public constructor(private readonly destroyRef: DestroyRef) {
    this.isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();
  }

  public async load(): Promise<void> {
    const authenticationConfiguration: IAuthenticationConfiguration = await firstValueFrom(
      this.configurationService.getAuthentication$(),
    );

    const authConfig: AuthConfig = {
      issuer: authenticationConfiguration.issuer,
      scope: authenticationConfiguration.scope,
      clientId: authenticationConfiguration.clientId,
      redirectUri: `${window.location.origin}/index.html`,
      postLogoutRedirectUri: `${window.location.origin}/index.html`,
      responseType: 'code',
      requireHttps: !isDevMode(),
      showDebugInformation: isDevMode(),
      preserveRequestedRoute: true,
      strictDiscoveryDocumentValidation: false,
    };

    this.oauthService.configure(authConfig);

    this.oauthService.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event: OAuthEvent) => event.type === 'invalid_nonce_in_state'),
      )
      .subscribe(() => {
        this.oauthService.initCodeFlow();
      });

    await this.oauthService.loadDiscoveryDocumentAndTryLogin();

    this.isAuthenticatedSubject$.next(this.isAuthenticated());

    if (this.isAuthenticated()) {
      this.oauthService.setupAutomaticSilentRefresh();
    } else {
      this.oauthService.initCodeFlow();
    }
  }

  public isAuthenticated(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  public getToken(): string {
    return this.oauthService.getAccessToken();
  }

  public getEmail(): string {
    const identityClaims: Record<string, AnyValue> = this.oauthService.getIdentityClaims();

    if (identityClaims) {
      return identityClaims['email'];
    }

    return '';
  }

  public logout(): void {
    this.oauthService.logOut();
  }
}
